<template>
  <div class="filters-row gap-2 mb-3 d-flex">
    <!-- Clear All -->
    <button v-if="hasActiveFilters" class="btn btn-sm btn-filter btn-clear-all" @click="onClearAll">
      <i class="material-icons me-1">clear</i>
      Clear All
    </button>

    <!-- Distance -->
    <div class="dropdown" data-bs-auto-close="outside">
      <button ref="distanceToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isDistanceFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
        Distance <i class="material-icons">expand_more</i>
        <span v-if="isDistanceFilterActive" class="filter-indicator"></span>
      </button>
      <div class="dropdown-menu p-3" style="min-width: 320px;" @click.stop @mousedown.stop>
        <div class="mb-2">
          <small v-if="distanceRangeDraft[1] >= distanceMaxBound">{{ $t('components.hike_filter_bar.min_distance_range_draft_distance_unit', { distance_range_draft: distanceRangeDraft[0], distance_unit_symbol: distanceUnitSymbol, distance_max_bound: distanceMaxBound, distance_unit_symbol_2: distanceUnitSymbol }) }}</small>
          <small v-else>{{ $t('components.hike_filter_bar.min_distance_range_draft_distance_unit_2', { distance_range_draft: distanceRangeDraft[0], distance_unit_symbol: distanceUnitSymbol, distance_range_draft_2: distanceRangeDraft[1], distance_unit_symbol_2: distanceUnitSymbol }) }}</small>
        </div>
        <VueSlider v-model="distanceRangeDraft" :min="0" :max="distanceMaxBound" :interval="1" :dot-size="16" :height="4" tooltip="none" />
        <div class="d-flex justify-content-between mt-2">
          <button class="btn btn-sm btn-link p-0" @click="resetAndClose(filters.resetDistance, distanceToggle)">{{ $t('shared.clear') }}</button>
          <button class="btn btn-sm btn-primary" @click="applyAndClose(filters.applyDistance, distanceToggle)">{{ $t('shared.apply') }}</button>
        </div>
      </div>
    </div>

    <!-- Elevation -->
    <div class="dropdown" data-bs-auto-close="outside">
      <button ref="elevationToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isElevationFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
        {{ $t('shared.elevation') }} <i class="material-icons">expand_more</i>
        <span v-if="isElevationFilterActive" class="filter-indicator"></span>
      </button>
      <div class="dropdown-menu p-3" style="min-width: 320px;" @click.stop @mousedown.stop>
        <div class="mb-2">
          <small v-if="elevationRangeDraft[1] >= elevationMaxBound">{{ $t('components.hike_filter_bar.min_elevation_range_draft_elevation_unit', { elevation_range_draft: elevationRangeDraft[0], elevation_unit_symbol: elevationUnitSymbol, elevation_max_bound: elevationMaxBound, elevation_unit_symbol_2: elevationUnitSymbol }) }}</small>
          <small v-else>{{ $t('components.hike_filter_bar.min_elevation_range_draft_elevation_unit_2', { elevation_range_draft: elevationRangeDraft[0], elevation_unit_symbol: elevationUnitSymbol, elevation_range_draft_2: elevationRangeDraft[1], elevation_unit_symbol_2: elevationUnitSymbol }) }}</small>
        </div>
        <VueSlider v-model="elevationRangeDraft" :min="0" :max="elevationMaxBound" :interval="50" :dot-size="16" :height="4" tooltip="none" />
        <div class="d-flex justify-content-between mt-2">
          <button class="btn btn-sm btn-link p-0" @click="resetAndClose(filters.resetElevation, elevationToggle)">{{ $t('shared.clear') }}</button>
          <button class="btn btn-sm btn-primary" @click="applyAndClose(filters.applyElevation, elevationToggle)">{{ $t('shared.apply') }}</button>
        </div>
      </div>
    </div>

    <!-- Difficulty -->
    <div class="dropdown" data-bs-auto-close="outside">
      <button ref="difficultyToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isDifficultyFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
        {{ $t('shared.difficulty') }} <i class="material-icons">expand_more</i>
        <span v-if="isDifficultyFilterActive" class="filter-indicator"></span>
      </button>
      <div class="dropdown-menu p-3" style="min-width: 260px;" @click.stop @mousedown.stop>
        <div class="mb-2"><small>{{ $t('components.hike_filter_bar.select_one_or_more') }}</small></div>
        <div class="mb-2" v-for="opt in DIFFICULTY_OPTIONS" :key="opt">
          <label class="d-flex align-items-center gap-2">
            <input type="checkbox" :value="opt" v-model="difficultyDraft" />
            <span>{{ opt }}</span>
          </label>
        </div>
        <div class="d-flex justify-content-between mt-2">
          <button class="btn btn-sm btn-link p-0" @click="resetAndClose(filters.resetDifficulty, difficultyToggle)">{{ $t('shared.clear') }}</button>
          <button class="btn btn-sm btn-primary" @click="applyAndClose(filters.applyDifficulty, difficultyToggle)">{{ $t('shared.apply') }}</button>
        </div>
      </div>
    </div>

    <!-- Features -->
    <div class="dropdown" data-bs-auto-close="outside">
      <button ref="featureToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isFeatureTagFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
        {{ featureTagButtonLabel }} <i class="material-icons">expand_more</i>
        <span v-if="isFeatureTagFilterActive" class="filter-indicator"></span>
      </button>
      <div class="dropdown-menu p-3" style="min-width: 260px;" @click.stop @mousedown.stop>
        <div class="mb-2"><small>{{ $t('components.hike_filter_bar.select_a_feature_tag') }}</small></div>
        <div class="mb-2">
          <select class="form-select" v-model="featureTagDraft">
            <option :value="null">{{ $t('components.hike_filter_bar.all_features') }}</option>
            <option v-for="tag in featureTags" :key="tag" :value="tag">{{ formatTagLabel(tag) }}</option>
          </select>
        </div>
        <div class="d-flex justify-content-between mt-2">
          <button class="btn btn-sm btn-link p-0" @click="resetAndClose(filters.resetFeatureTag, featureToggle)">{{ $t('shared.clear') }}</button>
          <button class="btn btn-sm btn-primary" @click="applyAndClose(filters.applyFeatureTag, featureToggle)">{{ $t('shared.apply') }}</button>
        </div>
      </div>
    </div>

    <!-- Activities -->
    <div class="dropdown" data-bs-auto-close="outside">
      <button ref="activityToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isActivityTagFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
        {{ activityTagButtonLabel }} <i class="material-icons">expand_more</i>
        <span v-if="isActivityTagFilterActive" class="filter-indicator"></span>
      </button>
      <div class="dropdown-menu p-3" style="min-width: 260px;" @click.stop @mousedown.stop>
        <div class="mb-2"><small>{{ $t('components.hike_filter_bar.select_an_activity_tag') }}</small></div>
        <div class="mb-2">
          <select class="form-select" v-model="activityTagDraft">
            <option :value="null">{{ $t('components.hike_filter_bar.all_activities') }}</option>
            <option v-for="tag in activityTags" :key="tag" :value="tag">{{ formatTagLabel(tag) }}</option>
          </select>
        </div>
        <div class="d-flex justify-content-between mt-2">
          <button class="btn btn-sm btn-link p-0" @click="resetAndClose(filters.resetActivityTag, activityToggle)">{{ $t('shared.clear') }}</button>
          <button class="btn btn-sm btn-primary" @click="applyAndClose(filters.applyActivityTag, activityToggle)">{{ $t('shared.apply') }}</button>
        </div>
      </div>
    </div>

    <!-- Date -->
    <div class="dropdown" data-bs-auto-close="outside">
      <button ref="dateToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isDateFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
        {{ $t('components.hike_filter_bar.date') }} <i class="material-icons">expand_more</i>
        <span v-if="isDateFilterActive" class="filter-indicator"></span>
      </button>
      <div class="dropdown-menu p-3" style="min-width: 320px;" @click.stop @mousedown.stop>
        <div class="mb-2"><small>{{ $t('shared.created_between') }}</small></div>
        <div class="d-flex gap-2 align-items-center">
          <input class="form-control" type="date" v-model="startDateDraft" />
          <span>–</span>
          <input class="form-control" type="date" v-model="endDateDraft" />
        </div>
        <div class="d-flex justify-content-between mt-2">
          <button class="btn btn-sm btn-link p-0" @click="resetAndClose(filters.resetDate, dateToggle)">{{ $t('shared.clear') }}</button>
          <button class="btn btn-sm btn-primary" @click="applyAndClose(filters.applyDate, dateToggle)">{{ $t('shared.apply') }}</button>
        </div>
      </div>
    </div>

    <!-- Sort -->
    <div class="dropdown" data-bs-auto-close="outside">
      <button ref="sortToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isSortFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
        {{ $t('shared.sort_by') }} <i class="material-icons">expand_more</i>
        <span v-if="isSortFilterActive" class="filter-indicator"></span>
      </button>
      <div class="dropdown-menu p-3" style="min-width: 260px;" @click.stop @mousedown.stop>
        <div class="mb-2"><small>{{ $t('shared.select_sort') }}</small></div>
        <div class="mb-2"><label class="d-flex align-items-center gap-2"><input type="radio" name="ch-sort" value="name_asc" v-model="sortDraft" /><span>{{ $t('components.hike_filter_bar.name_a_z') }}</span></label></div>
        <div class="mb-2"><label class="d-flex align-items-center gap-2"><input type="radio" name="ch-sort" value="created_desc" v-model="sortDraft" /><span>{{ $t('components.hike_filter_bar.most_recently_completed') }}</span></label></div>
        <div class="mb-2"><label class="d-flex align-items-center gap-2"><input type="radio" name="ch-sort" value="distance_desc" v-model="sortDraft" /><span>{{ $t('components.hike_filter_bar.distance_longest_first') }}</span></label></div>
        <div class="mb-2"><label class="d-flex align-items-center gap-2"><input type="radio" name="ch-sort" value="distance_asc" v-model="sortDraft" /><span>{{ $t('components.hike_filter_bar.distance_shortest_first') }}</span></label></div>
        <div class="d-flex justify-content-between mt-2">
          <button class="btn btn-sm btn-link p-0" @click="resetAndClose(filters.resetSort, sortToggle)">{{ $t('shared.reset') }}</button>
          <button class="btn btn-sm btn-primary" @click="applyAndClose(filters.applySort, sortToggle)">{{ $t('shared.apply') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { Dropdown } from 'bootstrap'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/dist-css/vue-slider-component.css'
import { useHikeFilters } from '../../composables/useHikeFilters'
import { i18n } from '../../i18n'

const t = i18n.global.t

const props = defineProps({
  featureTagOptions: { type: Array, default: () => [] },
  activityTagOptions: { type: Array, default: () => [] }
})
const emit = defineEmits(['change'])

const filters = useHikeFilters()
const {
  distanceMaxBound, elevationMaxBound, distanceUnitSymbol, elevationUnitSymbol,
  distanceRangeDraft, elevationRangeDraft, difficultyDraft,
  featureTagDraft, activityTagDraft, startDateDraft, endDateDraft, sortDraft,
  featureTags, activityTags, featureTagApplied, activityTagApplied, DIFFICULTY_OPTIONS,
  isDistanceFilterActive, isElevationFilterActive, isDifficultyFilterActive,
  isFeatureTagFilterActive, isActivityTagFilterActive, isDateFilterActive, isSortFilterActive,
  hasActiveFilters
} = filters

const distanceToggle = ref(null)
const elevationToggle = ref(null)
const difficultyToggle = ref(null)
const featureToggle = ref(null)
const activityToggle = ref(null)
const dateToggle = ref(null)
const sortToggle = ref(null)

const toggles = () => [distanceToggle, elevationToggle, difficultyToggle, featureToggle, activityToggle, dateToggle, sortToggle]

const featureTagButtonLabel = computed(() => featureTagApplied.value
  ? t('components.hike_filter_bar.feature_tag_applied', { tag: formatTagLabel(featureTagApplied.value) })
  : t('hikes.features'))
const activityTagButtonLabel = computed(() => activityTagApplied.value
  ? t('components.hike_filter_bar.activity_tag_applied', { tag: formatTagLabel(activityTagApplied.value) })
  : t('hikes.activities'))

function formatTagLabel (tag) {
  if (!tag) return ''
  return tag.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
}

// Keep the tag option lists in sync with whatever the host passes in.
watch(() => props.featureTagOptions, val => { featureTags.value = val || [] }, { immediate: true })
watch(() => props.activityTagOptions, val => { activityTags.value = val || [] }, { immediate: true })

function hide (toggleRef) {
  try {
    // In the template, refs are auto-unwrapped, so the Apply/Clear buttons pass
    // the DOM element itself (not the ref object) — reading `.value` on a
    // <button> returns "" and silently skipped the close. Accept either an
    // element or a raw ref so the dropdown reliably closes on Apply.
    const el = toggleRef?.nodeType ? toggleRef : toggleRef?.value
    if (el) Dropdown.getOrCreateInstance(el).hide()
  } catch (_) { /* noop */ }
}

function applyAndClose (applyFn, toggleRef) {
  applyFn()
  hide(toggleRef)
  emit('change', filters.buildFilterVariables())
}

function resetAndClose (resetFn, toggleRef) {
  resetFn()
  hide(toggleRef)
  emit('change', filters.buildFilterVariables())
}

function onClearAll () {
  filters.clearAll()
  emit('change', filters.buildFilterVariables())
}

const onDropdownShow = () => filters.syncDraftsFromApplied()

onMounted(() => {
  toggles().forEach(t => {
    if (!t.value) return
    Dropdown.getOrCreateInstance(t.value, {
      popperConfig: {
        strategy: 'fixed',
        modifiers: [
          { name: 'computeStyles', options: { adaptive: false } },
          { name: 'preventOverflow', options: { boundary: 'viewport', altBoundary: true, tether: false } }
        ]
      }
    })
    t.value.addEventListener('show.bs.dropdown', onDropdownShow)
  })
})

onBeforeUnmount(() => {
  toggles().forEach(t => {
    if (!t.value) return
    t.value.removeEventListener('show.bs.dropdown', onDropdownShow)
    try { Dropdown.getInstance(t.value)?.dispose() } catch (_) { /* noop */ }
  })
})

// The host reads the current filter variables when (re)fetching.
defineExpose({ buildVariables: filters.buildFilterVariables })
</script>

<style scoped>
/* Each filter button already shows a Material `expand_more` chevron, so hide
   Bootstrap's default dropdown caret to avoid a double down-arrow (matches the
   Hikes list page). */
.dropdown-toggle::after {
  display: none;
}
</style>
